import { DownloadOutlined } from "@ant-design/icons";
import { Button } from "antd/es";
import { saveAs } from "file-saver";
import _ from "lodash";
import Plotly from "plotly.js-dist-min";
import { PickerSharedProps } from "rc-picker/es/Picker";
import { PureComponent, ReactNode } from "react";
import "react-pivottable/pivottable.css";
import PivotTableUI from "react-pivottable/PivotTableUI";
import createPlotlyRenderers from "react-pivottable/PlotlyRenderers";
import TableRenderers from "react-pivottable/TableRenderers";
import createPlotlyComponent from "react-plotly.js/factory";
import { CSSProperties } from "styled-components";
import withCommonEvents from "../../../shared/hoc/with-common-events";
import { CommonProps } from "../common-props";

declare let window: any;
interface IPivotTableProps extends PickerSharedProps<any> {
  dataSource?: any[];
  onConfigChange?: () => void;
  style?: CSSProperties;
  readOnly?: boolean;
  export?: boolean;
}

interface IPivotTableState {
  pivotState: any;
}

const Plot = createPlotlyComponent(Plotly);
const PlotlyRenderers = createPlotlyRenderers(Plot);

class PivotTable extends PureComponent<IPivotTableProps & CommonProps, IPivotTableState> {
  private static instances: Map<string, PivotTable> = new Map();

  constructor(props: IPivotTableProps) {
    super(props);
    this.state = {
      pivotState: {
        data:
          this.props.dataSource && this.props.dataSource.length > 0
            ? this.convertToArrayFormat(this.props.dataSource)
            : [[]],
        unusedOrientationCutoff: Infinity
      }
    };
  }

  convertToArrayFormat = (data: any[]): any[][] => {
    const fields = Object.keys(data[0]);
    const values = data.map((obj) => Object.values(obj));
    return [fields, ...values];
  };

  componentDidMount = () => {
    if (this.props.id) {
      PivotTable.instances.set(this.props.id, this);
    }
  };

  componentWillUnmount() {
    if (this.props.id) {
      PivotTable.instances.delete(this.props.id);
    }
  }

  getProps = () => {
    const props: any = _.clone(this.props);
    const style: any = props.style || {};
    props.style = style;
    return props;
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.dataSource !== this.props.dataSource) {
      this.setState({
        pivotState: {
          ...this.state.pivotState,
          data:
            this.props.dataSource && this.props.dataSource.length > 0
              ? this.convertToArrayFormat(this.props.dataSource)
              : [[]],
          unusedOrientationCutoff: Infinity
        }
      });
    }
  }

  public getTableConfigJson = () => {
    const json = {
      pivotState: {
        data: this.state.pivotState.data,
        cols: this.state.pivotState.cols,
        rows: this.state.pivotState.rows,
        vals: this.state.pivotState.vals,
        aggregatorName: this.state.pivotState.aggregatorName,
        sorters: this.state.pivotState.sorters,
        valueFilter: this.state.pivotState.valueFilter,
        rowOrder: this.state.pivotState.rowOrder,
        colOrder: this.state.pivotState.colOrder,
        derivedAttributes: this.state.pivotState.derivedAttributes,
        tableOptions: this.state.pivotState.tableOptions,
        rendererName: this.state.pivotState.rendererName,
        // renderers: this.state.pivotState.renderers,
        hiddenAttributes: this.state.pivotState.hiddenAttributes,
        hiddenFromAggregators: this.state.pivotState.hiddenFromAggregators,
        hiddenFromDragDrop: this.state.pivotState.hiddenFromDragDrop,
        menuLimit: this.state.pivotState.menuLimit
      }
    };
    return JSON.stringify(json);
  };

  public setTableConfigJson = (json: string) => {
    if (!json || json === "") return;
    const obj = JSON.parse(json);
    this.setState({
      pivotState: {
        ...this.state.pivotState,
        ...obj.pivotState,
        unusedOrientationCutoff: Infinity
      }
    });
  };

  public resetTableConfigs = () => {
    this.setState({
      pivotState: {
        data: [[]]
      } as any
    });
  };

  public static exportTable = async (id: string): Promise<void> => {
    const instance = PivotTable.instances.get(id);
    if (instance) {
      await instance.exportPivotTable();
    } else {
      console.warn(`PivotTable with id "${id}" not found`);
    }
  };

  public exportPivotTable = async (): Promise<void> => {
    const { pivotState } = this.state;
    const data = pivotState.data;
    const rows = pivotState.rows || [];
    const cols = pivotState.cols || [];
    const vals = pivotState.vals || [];

    const usedColumns = [...rows, ...cols, ...vals];

    if (usedColumns.length === 0) {
      console.warn("No columns configured in pivot table");
      return;
    }

    if (!data || data.length === 0 || (data.length === 1 && data[0].length === 0)) {
      console.warn("No data to export");
      return;
    }

    const allHeaders = data[0];
    const usedColumnIndices = usedColumns
      .map((col: string) => allHeaders.indexOf(col))
      .filter((index: number) => index !== -1);

    if (usedColumnIndices.length === 0) {
      console.warn("No matching columns found in data");
      return;
    }

    const filteredHeaders = usedColumnIndices.map((index: number) => allHeaders[index]);

    const ExcelJS = (await import("exceljs")).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("PivotTable");

    worksheet.columns = filteredHeaders.map((header: string, index: number) => ({
      header: header || `Column ${index + 1}`,
      key: `col${index}`,
      width: Math.max(15, (header?.toString()?.length || 10) + 5)
    }));

    const headerRow = worksheet.getRow(1);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF4472C4" }
    };
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };
    headerRow.height = 25;

    for (let i = 1; i < data.length; i++) {
      const rowData: { [key: string]: any } = {};
      usedColumnIndices.forEach((originalIndex: number, newIndex: number) => {
        rowData[`col${newIndex}`] = data[i][originalIndex];
      });
      const row = worksheet.addRow(rowData);

      if (i % 2 === 0) {
        row.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFF2F2F2" }
        };
      }
    }

    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFD0D0D0" } },
          left: { style: "thin", color: { argb: "FFD0D0D0" } },
          bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
          right: { style: "thin", color: { argb: "FFD0D0D0" } }
        };
      });
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell?.({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value ? cell.value.toString() : "";
        const cellLength = cellValue.length;
        if (cellLength > maxLength) {
          maxLength = cellLength;
        }
      });
      column.width = Math.max(10, Math.min(maxLength + 2, 50));
    });

    workbook.xlsx
      .writeBuffer()
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        saveAs(blob, "pivot-table-export.xlsx");
      })
      .catch((error) => {
        console.error("Error saving the file:", error);
      });
  };

  render(): ReactNode {
    const isDesignTime = window.kuika?.isDesignTime;
    const { readOnly } = this.props;
    const language = window.kuika?.dashboardState?.Language || "en_US";

    const lockedStyles: CSSProperties = readOnly
      ? {
          pointerEvents: "none",
          userSelect: "none"
        }
      : {};

    return (
      <div>
        {this.props.export && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 8,
              padding: "0 8px"
            }}
          >
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => this.exportPivotTable()}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4
              }}
            >
              {language === "tr_TR" ? "Dışa Aktar" : "Export"}
            </Button>
          </div>
        )}
        <div className="row">
          <div style={lockedStyles}>
            <PivotTableUI
              {...this.state.pivotState}
              onChange={(s) => {
                if (readOnly) return;
                this.setState({ pivotState: s }, () => {
                  this.props.onConfigChange && this.props.onConfigChange();
                });
              }}
              renderers={{ ...TableRenderers, ...PlotlyRenderers }}
            />
          </div>
        </div>
      </div>
    );
  }
}
const pivotTable = withCommonEvents(PivotTable);
export { pivotTable as PivotTable };
